import { useCallback, useMemo, useState } from "react";

import {
  applySale,
  emptyHustleResult,
  finalizeHustle,
  resolveSale,
} from "./engine";
import { generateRun, randomSeed } from "./generate";
import type { AcceptMethod, Customer, HustleResult, RoundConditions } from "./types";

export type HustlePhase = "playing" | "finished";

interface HustleState {
  phase: HustlePhase;
  index: number;
  seed: number;
  customers: Customer[];
  conditions: RoundConditions[];
  result: HustleResult;
}

function initialState(seed = randomSeed()): HustleState {
  const run = generateRun(seed);
  return {
    phase: "playing",
    index: 0,
    seed: run.seed,
    customers: run.customers,
    conditions: run.conditions,
    result: emptyHustleResult(),
  };
}

export function useHustle() {
  const [state, setState] = useState<HustleState>(() => initialState());

  const currentCustomer: Customer | undefined = state.customers[state.index];
  const currentConditions: RoundConditions | undefined = state.conditions[state.index];

  const accept = useCallback((method: AcceptMethod) => {
    setState((prev) => {
      const customer = prev.customers[prev.index];
      const conditions = prev.conditions[prev.index];
      if (!customer || !conditions) return prev;

      const sale = resolveSale(customer, method, conditions);
      const result = applySale(prev.result, sale);
      const nextIndex = prev.index + 1;
      const finished = nextIndex >= prev.customers.length;

      return {
        ...prev,
        phase: finished ? "finished" : "playing",
        index: nextIndex,
        result: finished ? finalizeHustle(result) : result,
      };
    });
  }, []);

  // A brand-new randomized run each time.
  const restart = useCallback(() => setState(initialState()), []);

  const progress = useMemo(
    () => ({ round: state.index + 1, totalRounds: state.customers.length }),
    [state.index, state.customers.length],
  );

  return {
    phase: state.phase,
    currentCustomer,
    currentConditions,
    result: state.result,
    progress,
    accept,
    restart,
  };
}
